import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Container, Stack, Text, Paper, Box, Loader, Button, Group, Badge } from '@mantine/core'
import { IconArrowLeft, IconRefresh, IconMessageDots, IconPointFilled } from '@tabler/icons-react'
import { playerSessionService } from '../services/playerSessionService'
import { storyService } from '../services/storyService'

/**
 * Condensed, read-only recap of a completed session: each thread title followed
 * by the player's own answers (choices and typed responses) in order. A light
 * companion to the AI Analysis page — just "what you said", nothing generated.
 *
 * Data: one session fetch (responses + story_name) joined to one threads fetch
 * (for thread titles). Answer text is self-contained in each response, so no
 * per-beat lookups are needed.
 */

// Only the player's own inputs belong in a recap — skip narrative/AI beats.
const ANSWER_TYPES = new Set(['choice', 'text'])

function answerText(response) {
  const d = response?.response_data || {}
  if (response.response_type === 'choice') return d.choice_text || '(choice)'
  if (response.response_type === 'text') return d.text || ''
  return ''
}

function AnswerRow({ response }) {
  const isChoice = response.response_type === 'choice'
  return (
    <Box
      style={{
        padding: '10px 14px',
        borderRadius: 6,
        background: 'rgba(10, 22, 40, 0.5)',
        borderLeft: `3px solid ${isChoice ? 'rgba(255, 159, 64, 0.5)' : 'rgba(0, 229, 255, 0.4)'}`,
      }}
    >
      <Group gap={8} mb={2} wrap="nowrap">
        <Badge size="xs" variant="light" color={isChoice ? 'orange' : 'cyan'}>
          {isChoice ? 'Choice' : 'Response'}
        </Badge>
      </Group>
      <Text
        size="sm"
        c={isChoice ? 'orange.2' : 'cyan.2'}
        ff="Rajdhani, sans-serif"
        fw={500}
        style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
      >
        {isChoice ? answerText(response) : `"${answerText(response)}"`}
      </Text>
    </Box>
  )
}

export default function SessionSummary() {
  const { sessionId } = useParams()
  const navigate = useNavigate()

  const {
    data: session,
    isLoading: sessionLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['playerSession', sessionId],
    queryFn: () => playerSessionService.getPlayerSession(sessionId),
    enabled: !!sessionId,
  })

  const storyId = session?.story_id

  const { data: threadsResponse, isLoading: threadsLoading } = useQuery({
    queryKey: ['storyThreads', storyId, 'summary'],
    queryFn: () => storyService.getStoryThreads(storyId, { limit: 1000 }),
    enabled: !!storyId,
  })

  // Group the player's answers under each thread, in thread order. Threads with
  // no recorded answers (pure narrative) are dropped so the recap stays tight.
  const sections = useMemo(() => {
    if (!session) return []
    const answers = (session.responses || []).filter((r) => ANSWER_TYPES.has(r.response_type))

    const byThread = new Map()
    for (const r of answers) {
      const key = r.thread_id || '__none__'
      if (!byThread.has(key)) byThread.set(key, [])
      byThread.get(key).push(r)
    }

    const threads = (threadsResponse?.data || [])
      .filter((t) => t.active !== false)
      .sort((a, b) => a.position - b.position)

    const ordered = []
    const seen = new Set()
    threads.forEach((t, i) => {
      const items = byThread.get(t.id)
      if (items?.length) {
        ordered.push({ id: t.id, title: t.name || `Chapter ${i + 1}`, chapter: i + 1, items })
        seen.add(t.id)
      }
    })
    // Any answers whose thread isn't in the (possibly still-loading) thread list
    // are appended so nothing the player said is silently dropped.
    for (const [key, items] of byThread) {
      if (key !== '__none__' && !seen.has(key)) {
        ordered.push({ id: key, title: 'Chapter', chapter: null, items })
      }
    }
    return ordered
  }, [session, threadsResponse])

  const isLoading = sessionLoading || (!!storyId && threadsLoading)

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Loader size="md" color="cyan" />
          <Text c="dimmed" size="sm">Loading your summary...</Text>
        </Stack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text c="red.4" fw={600} ff="Orbitron, sans-serif">Summary Unavailable</Text>
          <Text c="dimmed" size="sm" ta="center" maw={400}>
            {error.response?.status === 404
              ? 'This session could not be found.'
              : 'Unable to load this session summary. Please try again.'}
          </Text>
          <Group gap="sm">
            <Button variant="light" color="gray" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/profile')}>
              Back to Profile
            </Button>
            <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()}>
              Retry
            </Button>
          </Group>
        </Stack>
      </Container>
    )
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/profile')}
          style={{ alignSelf: 'flex-start' }}
          size="sm"
        >
          Back to Profile
        </Button>

        {/* Header */}
        <Paper p="lg" className="glass" style={{ borderColor: 'rgba(0, 229, 255, 0.2)', textAlign: 'center' }}>
          <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={2} mb={4}>
            Session Summary
          </Text>
          <Text size="xl" fw={700} ff="Orbitron, sans-serif" c="cyan.3" className="glow-text-cyan">
            {session?.story_name || 'Your Playthrough'}
          </Text>
          {session?.completed_at && (
            <Text size="xs" c="dimmed" mt={4}>
              Completed {new Date(session.completed_at).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </Text>
          )}
        </Paper>

        {sections.length === 0 ? (
          <Paper p="lg" className="glass">
            <Group gap="sm" justify="center" py="md">
              <IconMessageDots size={20} color="var(--mantine-color-dimmed)" />
              <Text size="sm" c="dimmed">No recorded answers for this session.</Text>
            </Group>
          </Paper>
        ) : (
          sections.map((section) => (
            <Paper key={section.id} p="lg" className="glass">
              <Group gap={8} mb="md" wrap="nowrap">
                <IconPointFilled size={14} color="var(--mantine-color-cyan-4)" />
                <Text fw={600} size="sm" c="cyan.3" ff="Orbitron, sans-serif">
                  {section.chapter ? `Chapter ${section.chapter}: ` : ''}{section.title}
                </Text>
              </Group>
              <Stack gap="sm">
                {section.items.map((item, i) => (
                  <AnswerRow key={`${item.beat_id}-${i}`} response={item} />
                ))}
              </Stack>
            </Paper>
          ))
        )}
      </Stack>
    </Container>
  )
}
